/**
 * The narrowest bucket the thinning considers. A Spotter reports at minute resolution at its fastest, so a
 * bucket this wide holds one position and thinning at this width hands back what it was given.
 */
const MIN_BUCKET_WIDTH_MS = 60_000;

/**
 * Keeps at most `maxItems` spread across their time span. `maxItems` must be at least two.
 *
 * Time is split into epoch-aligned buckets, keeping the oldest item in each. Bucket widths are powers of two
 * times a fixed base, so wider buckets align with narrower ones and keep a subset of their items.
 *
 * This keeps existing points stable as newer items arrive: a selected item either remains selected or drops
 * out. Items may be in either time order, and selected items retain their input order. Invalid timestamps all
 * share one bucket.
 */
export const thinByTimeBucket = <TItem extends { timestamp: string }>(items: TItem[], maxItems: number): TItem[] => {
    if (items.length <= maxItems) {
        return items;
    }

    const timesMs = items.map(({ timestamp }) => new Date(timestamp).getTime());

    let earliestMs = Infinity;
    let latestMs = -Infinity;
    timesMs.forEach((timeMs) => {
        if (!Number.isFinite(timeMs)) {
            return;
        }
        earliestMs = Math.min(earliestMs, timeMs);
        latestMs = Math.max(latestMs, timeMs);
    });
    const spanMs = Number.isFinite(earliestMs) ? latestMs - earliestMs : 0;

    /** A bucket at least as wide as the span splits the items across two buckets at the most. */
    const widestExponent = Math.ceil(Math.log2(Math.max(spanMs, MIN_BUCKET_WIDTH_MS) / MIN_BUCKET_WIDTH_MS));

    const keptIndicesAt = (exponent: number): number[] => {
        const bucketWidthMs = MIN_BUCKET_WIDTH_MS * 2 ** exponent;
        const oldestIndexByBucket = new Map<number, number>();
        timesMs.forEach((timeMs, index) => {
            const bucket = Math.floor(timeMs / bucketWidthMs);
            const heldIndex = oldestIndexByBucket.get(bucket);
            if (heldIndex === undefined || timeMs < timesMs[heldIndex]) {
                oldestIndexByBucket.set(bucket, index);
            }
        });
        return Array.from(oldestIndexByBucket.values()).sort((left, right) => left - right);
    };

    /*
     * The count a width keeps falls as the width grows, so the narrowest width that fits is the boundary of a
     * monotone predicate and a binary search finds it. `keptIndices` holds the answer for `widestExponent`,
     * which is known to fit.
     */
    let narrowest = 0;
    let widest = widestExponent;
    let keptIndices = keptIndicesAt(widest);
    while (narrowest < widest) {
        const middle = Math.floor((narrowest + widest) / 2);
        const candidate = keptIndicesAt(middle);
        if (candidate.length <= maxItems) {
            widest = middle;
            keptIndices = candidate;
        } else {
            narrowest = middle + 1;
        }
    }

    return keptIndices.map((index) => items[index]);
};
